import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import {
  Card,
  Button,
  message,
  Row,
  Col,
  Typography,
  Input,
  Select,
  Form,
  Switch,
  InputNumber,
  Divider,
  Alert,
  Flex,
  Tabs,
  theme
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  ApiOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';

const { Text } = Typography;
const { Option } = Select;

type AISettings = {
  modelProvider: string;
  apiKey: string;
  maxTokens: number;
  temperature: number;
  timeout: number;
  retryAttempts: number;
  enableLogging: boolean;
  enableCaching: boolean;
  cacheExpiry: number;
  enableRateLimit: boolean;
  rateLimitPerMinute: number;
  enableMonitoring: boolean;
  alertThreshold: number;
};

function AIGeneralSettingsPage() {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const [settings, setSettings] = useState<AISettings>({
    modelProvider: 'openai',
    apiKey: 'sk-***hidden***',
    maxTokens: 4000,
    temperature: 0.7,
    timeout: 30,
    retryAttempts: 3,
    enableLogging: true,
    enableCaching: true,
    cacheExpiry: 3600,
    enableRateLimit: true,
    rateLimitPerMinute: 60,
    enableMonitoring: true,
    alertThreshold: 80
  });

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      setSettings(prev => ({ ...prev, ...values }));
      message.success(t('ai.general.settingsSaved'));
    } catch (error) {
      message.error(t('ai.general.checkInput'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    message.info(t('ai.general.settingsReset'));
  };

  const stats = {
    totalRequests: 15420,
    successRate: 98.5,
    averageResponseTime: 1.2,
    activeModels: 3
  };

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<SettingOutlined />}
        title={t('ai.general.title')}
        description={t('ai.general.subtitle')}
      />

      <StatCardGrid>
        <StatCard label={t('ai.general.totalRequests')} value={stats.totalRequests.toLocaleString()} icon={<ApiOutlined />} />
        <StatCard
          label={t('ai.general.successRate')}
          value={`${stats.successRate}%`}
          icon={<CheckCircleOutlined />}
          color={token.colorSuccess}
        />
        <StatCard
          label={t('ai.general.avgResponseTime')}
          value={`${stats.averageResponseTime}s`}
          icon={<FieldTimeOutlined />}
          color={token.colorInfo}
        />
        <StatCard label={t('ai.general.activeModels')} value={stats.activeModels} icon={<RobotOutlined />} color={token.purple} />
      </StatCardGrid>

      <Card>
        <Tabs 
          defaultActiveKey="api"
          items={[
            {
              key: 'api',
              label: t('ai.general.tabs.api'),
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={settings}
                  onFinish={handleSave}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="modelProvider"
                        label={t('ai.general.fields.modelProvider')}
                        rules={[{ required: true, message: t('ai.general.fields.modelProviderRequired') }]}
                      >
                        <Select placeholder={t('ai.general.fields.modelProviderPlaceholder')}>
                          <Option value="openai">OpenAI</Option>
                          <Option value="anthropic">Anthropic</Option>
                          <Option value="google">Google</Option>
                          <Option value="azure">Azure OpenAI</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="apiKey"
                        label={t('ai.general.fields.apiKey')}
                        rules={[{ required: true, message: t('ai.general.fields.apiKeyRequired') }]}
                      >
                        <Input.Password placeholder={t('ai.general.fields.apiKeyPlaceholder')} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="maxTokens"
                        label={t('ai.general.fields.maxTokens')}
                        rules={[{ required: true, message: t('ai.general.fields.maxTokensRequired') }]}
                      >
                        <InputNumber
                          min={1}
                          max={8000}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.maxTokensPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="temperature"
                        label={t('ai.general.fields.temperature')}
                        rules={[{ required: true, message: t('ai.general.fields.temperatureRequired') }]}
                      >
                        <InputNumber
                          min={0}
                          max={2}
                          step={0.1}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.temperaturePlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="timeout"
                        label={t('ai.general.fields.requestTimeout')}
                        rules={[{ required: true, message: t('ai.general.fields.timeoutRequired') }]}
                      >
                        <InputNumber
                          min={5}
                          max={300}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.timeoutPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="retryAttempts"
                        label={t('ai.general.fields.retryAttempts')}
                        rules={[{ required: true, message: t('ai.general.fields.retryAttemptsRequired') }]}
                      >
                        <InputNumber
                          min={0}
                          max={10}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.retryAttemptsPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )
            },
            {
              key: 'performance',
              label: t('ai.general.tabs.performance'),
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={settings}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="enableCaching"
                        label={t('ai.general.fields.enableCaching')}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="cacheExpiry"
                        label={t('ai.general.fields.cacheExpiry')}
                        rules={[{ required: true, message: t('ai.general.fields.cacheExpiryRequired') }]}
                      >
                        <InputNumber
                          min={60}
                          max={86400}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.cacheExpiryPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="enableRateLimit"
                        label={t('ai.general.fields.enableRateLimit')}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="rateLimitPerMinute"
                        label={t('ai.general.fields.rateLimit')}
                        rules={[{ required: true, message: t('ai.general.fields.rateLimitRequired') }]}
                      >
                        <InputNumber
                          min={1}
                          max={1000}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.rateLimitPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )
            },
            {
              key: 'monitoring',
              label: t('ai.general.tabs.monitoring'),
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={settings}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="enableLogging"
                        label={t('ai.general.fields.enableLogging')}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="enableMonitoring"
                        label={t('ai.general.fields.enableMonitoring')}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="alertThreshold"
                        label={t('ai.general.fields.alertThreshold')}
                        rules={[{ required: true, message: t('ai.general.fields.alertThresholdRequired') }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                          placeholder={t('ai.general.fields.alertThresholdPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )
            },
            {
              key: 'security',
              label: t('ai.general.tabs.security'),
              children: (
                <>
                  <Alert
                    title={t('ai.general.security.title')}
                    description={t('ai.general.security.description')}
                    type="info"
                    showIcon
                    style={{ marginBottom: token.marginMD }}
                  />
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={settings}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <Form.Item
                          name="enableLogging"
                          label={t('ai.general.fields.logAllRequests')}
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item
                          name="enableCaching"
                          label={t('ai.general.fields.enableEncryption')}
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item
                          name="enableRateLimit"
                          label={t('ai.general.fields.enableIpWhitelist')}
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </>
              )
            }
          ]}
        />

        <Divider />

        <Flex justify="space-between" align="center" wrap="wrap" gap={token.marginSM}>
          <Text type="secondary" style={{ flex: '1 1 240px' }}>
            {t('ai.general.saveNotice')}
          </Text>
          <Flex gap={token.marginXS} wrap="wrap" justify="flex-end">
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              {t('ai.general.reset')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={loading}
            >
              {t('ai.general.saveSettings')}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </PageTransition>
  );
}

export const Route = createFileRoute('/admin/ai/general')({
  component: AIGeneralSettingsPage
});

